import { captureShellRenderState, restoreActionableToasts, restoreShellRenderState, retainActionableToasts, type ShellRenderState } from "../ui/shell-render-controller.ts";
import { applicationShellView, type ApplicationShellViewOptions } from "./shell-view.ts";

export interface ShellRenderSession {
  startedAt: number;
  actionableToasts: HTMLElement[];
}

export interface ShellRenderRuntimeCapabilities {
  document: Document;
  target: HTMLElement;
  scrollY(): number;
  scrollTo(scrollTop: number): void;
  now(): number;
  rememberSidebar(): void;
  restoreSidebar(sidebar: HTMLElement, scrollTop: number): void;
  syncCalendarHeaderScroll(calendar: HTMLElement): void;
  setPanelFocus(open: boolean): void;
  setTaskMoreOpen(open: boolean): void;
  updateBackToTopVisibility(): void;
  enableBackGuard(): void;
  bindEvents(): void;
  requestFrame(callback: FrameRequestCallback): number;
  measure(name: string, startedAt: number): void;
}

export function createShellRenderRuntime(capabilities: ShellRenderRuntimeCapabilities) {
  function begin(): ShellRenderSession {
    const session = { startedAt: capabilities.now(), actionableToasts: retainActionableToasts(capabilities.document) };
    capabilities.rememberSidebar();
    return session;
  }

  function capture(fallbackTaskMoreOpen: boolean): ShellRenderState {
    return captureShellRenderState(capabilities.document, capabilities.scrollY(), fallbackTaskMoreOpen);
  }

  function commit(session: ShellRenderSession, state: ShellRenderState, options: ApplicationShellViewOptions, selected: boolean): void {
    capabilities.document.title = `${options.viewTitle} · WeirDo`;
    capabilities.target.innerHTML = applicationShellView(options);
    restoreActionableToasts(capabilities.document, session.actionableToasts);
    capabilities.setPanelFocus(selected);
    capabilities.bindEvents();
    restoreShellRenderState(state, {
      document: capabilities.document,
      selected,
      restoreSidebar: capabilities.restoreSidebar,
      syncCalendarHeaderScroll: capabilities.syncCalendarHeaderScroll,
      setTaskMoreOpen: capabilities.setTaskMoreOpen,
      updateBackToTopVisibility: capabilities.updateBackToTopVisibility,
      enableBackGuard: capabilities.enableBackGuard,
      requestFrame: capabilities.requestFrame,
      restoreWorkspaceScroll: capabilities.scrollTo,
    });
    capabilities.measure("shell-render", session.startedAt);
  }

  return { begin, capture, commit };
}
